import type { TranslationSchema } from '../pt-BR';
import type { DeepPartialStrings } from '../types';

export const transactions = {
    layout: {
        newFinancingTitle: 'New Financing',
        newTransactionTitle: 'New Transaction',
        editOccurrenceTitle: 'Edit occurrence',
        editTransactionTitle: 'Edit Transaction',
        monthScreenTitle: "This month's transactions",
        recentScreenTitle: 'Recent expenses',
        newCategoryTitle: 'New Category',
    },
    list: {
        title: 'Transactions',
        searchPlaceholder: 'Search by name or amount...',
        searchScopeMonth: 'This month',
        searchScopeAll: 'All time',
        typeAll: 'All',
        typeIncome: 'Income',
        typeExpense: 'Expenses',
        typeTransfer: 'Transfers',
        completedSection: 'Completed',
        overdueSection: 'Overdue',
        thisMonthSection: 'This month',
        dayBalance: 'Day balance {{value}}',
        dayProjectedBalance: 'Projected balance {{value}}',
        emptyTitle: 'No transactions',
        emptySubtitle: 'Use the + button to add',
        emptySearchTitle: 'No results',
        emptySearchTryAnotherTerm: 'Try another term',
        emptySearchTryAllTime: 'Try searching "All time"',
    },
    selection: {
        cancel: 'Cancel',
        selectAll: 'All',
        selectedCount_one: '{{count}} selected',
        selectedCount_other: '{{count}} selected',
    },
    calendar: {
        toggleA11y: 'View cash flow calendar',
        listA11y: 'View transaction list',
        weekdaySun: 'S',
        weekdayMon: 'M',
        weekdayTue: 'T',
        weekdayWed: 'W',
        weekdayThu: 'T',
        weekdayFri: 'F',
        weekdaySat: 'S',
        includePendingTitle: 'Include not settled',
        includePendingChip: 'Not settled',
        includePendingHint: 'Adds pending entries, upcoming recurrences and open card invoices.',
        modeBalance: 'Balance',
        modeMovement: 'Activity',
        dayA11y: 'Day {{day}}, balance {{amount}}',
        dayMovementA11y: 'Day {{day}}, activity {{amount}}',
        detailClose: 'Close',
        detailNoDelta: 'No activity',
        detailDayMovement: 'Day activity',
        detailDayEnd: 'End of day',
        addOnThisDay: 'Add on this day',
        locked: {
            badge: 'PRO',
            title: 'Find the day your cash runs out',
            subtitle: "Every day's balance for the month, including bills and card invoices that haven't hit yet.",
            cta: 'Unlock with Pro',
            dismiss: 'Not now',
        },
        emptyTitle: 'No accounts to add up',
        emptySubtitle: 'The calendar uses the accounts included in your total balance. Include at least one to see the flow.',
    },
    batch: {
        category: 'Category',
        tag: 'Tag',
        delete: 'Delete',
        deleteDialog: {
            title: 'Bulk delete',
            message: 'Delete {{total}} transactions?',
            confirm: 'Delete',
        },
        tagSheet: {
            title: 'Add tag',
            message: 'Select the tag:',
        },
    },
    recurringDeleteSheet: {
        title: 'Delete transaction',
        message: 'This transaction repeats. What do you want to delete?',
        onlyThis: 'Only this one',
        onlyThisDescription: 'Removes only this entry.',
        thisAndNext: 'This and the next',
        thisAndNextDescription: 'Deletes this occurrence and the following ones.',
        all: 'All',
        allDescription: 'Deletes the entire recurring series.',
    },
    month: {
        titlePay: 'Pay',
        titleReceive: 'Receive',
        titleInvoices: 'Statements',
        titleTransfers: 'Scheduled transfers',
        titleTagFallback: 'Tag',
        titleOthers: 'Others',
        titleCategoryFallback: 'Category',
        titleStatement: 'Transactions',
        titleIncome: 'Income',
        titleExpense: 'Expenses',
        confirmedLabel: 'Confirmed',
        receivedLabel: 'Received',
        paidLabel: 'Paid',
        confirmedOfTotal: '{{label}}: {{amount}}',
        transactionCount_one: '{{count}} transaction',
        transactionCount_other: '{{count}} transactions',
        filterSettled: 'Settled',
        filterPending: 'Pending',
        filterAccounts_one: '{{count}} account',
        filterAccounts_other: '{{count}} accounts',
        filterCategories_one: '{{count}} category',
        filterCategories_other: '{{count}} categories',
        completedSection: 'Completed',
        periodMonth: 'this month',
        periodCustom: 'this period',
        searchPlaceholder: 'Search this list...',
        empty: {
            allTitle: 'No transactions',
            invoicesTitle: 'No statements',
            incomeTitle: 'No income',
            expenseTitle: 'No expenses',
            allSubtitle: 'No transactions {{period}}',
            invoicesSubtitle: 'No statements {{period}}',
            incomeSubtitle: 'No income {{period}}',
            expenseSubtitle: 'No expenses {{period}}',
            searchTitle: 'No results',
            searchSubtitle: 'Nothing with that name or amount in this list',
        },
    },
    recent: {
        emptyText: 'No expenses found',
        searchPlaceholder: 'Search this list...',
        emptySearchText: 'None of the recent expenses match that name or amount',
        installmentBadge: '{{times}}x',
    },
    form: {
        defaults: {
            transferDescription: 'Transfer between accounts',
        },
        title: {
            duplicate: 'Duplicate entry',
            newRefund: 'New refund',
            newFinancing: 'New Financing',
            newLoanContract: 'New Loan',
            editFinancing: 'Edit financing',
            editLoanContract: 'Edit loan',
            newLoan: 'New receivable',
            newCardExpense: 'New Card Expense',
            newIncome: 'New Income',
            newTransfer: 'New Transfer',
            newExpense: 'New Expense',
            editExpense: 'Edit expense',
            editIncome: 'Edit income',
            editTransfer: 'Edit transfer',
        },
        amountLabel: {
            refund: 'Amount refunded',
            financing: 'Monthly installment amount',
            loan: 'Amount owed',
            income: 'Income amount',
            transfer: 'Transfer amount',
            expense: 'Expense amount',
            installment: 'Installment amount',
        },
        installmentBadge: {
            a11y: 'Installment {{current}} of {{total}}',
            a11yPurchase: 'Installment {{current}} of {{total}}, {{amount}} purchase on {{date}}',
            purchase: 'Purchase',
            madeOn: 'Made on',
            recorded: 'In the app',
            lastInstallment: 'Last installment',
            partialHint: 'Added starting at installment {{first}}',
        },
        dateFallback: 'Date',
        action: {
            registerRefund: 'Add refund',
            createFinancing: 'Create financing',
            createLoanContract: 'Create loan',
            saveContractChanges: 'Save contract',
            registerLoan: 'Register receivable',
            saveOccurrence: 'Save occurrence',
            saveAndCreateRecurrence: 'Save and create recurrence',
            saveChanges: 'Save changes',
            createRecurrence: 'Create recurrence',
            transfer: 'Transfer',
            save: 'Save',
            saveAndContinue: 'Save and continue',
        },
        a11y: {
            duplicateTransaction: 'Duplicate entry',
            deleteTransaction: 'Delete entry',
            importReceipt: 'Import receipt',
        },
        confirmedToggle: {
            received: 'Received',
            paid: 'Paid',
        },
        fields: {
            timeLabel: 'Purchase time',
            timeLabelIncome: 'Time received',
            timeLabelTransfer: 'Transfer time',
            timeLabelRefund: 'Refund time',
            descriptionPlaceholder: 'Description (optional)',
            contractNamePlaceholder: 'Contract name',
            category: 'Category',
            selectCategoryPlaceholder: 'Select category...',
            suggestedCategory: 'Suggested category: {{category}}',
            suggestedCategoryChange: 'Change',
            creditCard: 'Credit card',
            selectCardPlaceholder: 'Select card...',
            account: 'Account',
            selectAccountPlaceholder: 'Select account...',
            sourceAccount: 'Source account',
            destinationAccount: 'Destination account',
            notesPlaceholder: 'Notes',
            tags: 'Tags',
        },
        details: {
            show: 'More details',
            hide: 'Hide details',
        },
        financing: {
            toggle: 'Financing or loan',
            kindFinancing: 'Financing',
            kindLoan: 'Loan',
            kindFinancingHint: 'A financed asset — property, car, motorbike. The instalments are already set.',
            kindLoanHint: 'Money you borrowed from a bank or fintech. The instalment can stay open and accrue interest.',
            monthlyHint: 'How much you pay per month — not the contract total.',
            typeSheetTitle: 'What kind of contract?',
            typeSheetSubtitle: 'Kontto starts tracking the instalments: schedule, outstanding balance and payoff.',
            typeSheetKeepAsExpense: 'Keep as a regular expense',
            contractRowTitle: 'This is an instalment contract',
            contractRowSubtitle: 'Financing or loan, with instalments to come',
            contractChip: 'Instalment contract',
            newFinancingTitle: 'New financing',
            newLoanTitle: 'New loan',
            typeRowLabel: 'Type',
            calcRowLabel: 'Calculation',
            calcSheetTitle: 'How the instalment behaves',
            calcSheetSubtitle: 'If you are not sure, leave it on Fixed instalment — you can change it later without losing history.',
            calcSheetConfirm: 'Use {{system}}',
            systemDefaultBadge: 'default',
            fineTuningLabel: 'Interest, insurance and balance',
            rateFieldLabel: 'Contract interest',
            rateFieldUnit: '% per year',
            insuranceFieldLabel: 'Insurance and fees',
            insuranceFieldNote: 'MIP/DFI',
            insuranceFieldUnit: '/mo',
            balanceFieldLabel: 'Outstanding balance today',
            balanceTodayTip: 'Calculated from the {{balance}} entered before instalment {{number}}. Changing it recalculates the upcoming instalments.',
            summaryTitle: 'How it will look',
            summaryFixed: '{{count}} instalments left · last in {{month}} · {{total}} to pay',
            summarySac: 'Instalment drops from {{from}} to {{to}} by {{month}}',
            createFinancingCta: 'Create financing',
            createLoanCta: 'Create loan',
            systemLabel: 'Amortisation system',
            systemFixed: 'Fixed instalment',
            systemPrice: 'Price',
            systemSac: 'SAC',
            systemFixedHint: 'Every instalment is worth the same. Use it when the bank did not say which system applies — no rate needed.',
            systemPriceHint: 'Constant instalment to the end, but the app splits interest from principal. Asks for the contract rate.',
            systemSacHint: 'The instalment drops a little every month. Common in mortgages. Asks for rate and outstanding balance.',
            annualRateLabel: 'Contract rate (% per year)',
            annualRatePlaceholder: 'e.g. 8.99',
            annualRateHint: 'Per YEAR, as the bank quotes it. If the contract says "TR + 8.99% p.a.", enter just 8.99.',
            extraAmountLabel: 'Insurance and fees per month (optional)',
            extraAmountHint: 'Insurance and admin fees are not in the formula — enter them here, or adjust later when the bill arrives.',
            curveBalanceHint: 'The outstanding balance is the basis for the declining instalment.',
            institution: 'Institution',
            institutionPlaceholder: 'Institution name (optional)',
            totalInstallments: 'Total installments',
            totalInstallmentsPlaceholder: 'e.g. 60',
            nextInstallment: 'Next installment',
            nextInstallmentPlaceholder: 'e.g. 12',
            dueDateLabel: 'Next installment due date',
            dueDayPill: 'every {{day}}th',
            installmentPairLabel: 'Instalment',
            installmentPairOf: 'of',
            dueDateHint: 'Not the contract date: installments are due on day {{day}} every month.',
            dueDateHintShortMonths: 'Not the contract date: installments are due on day {{day}} — or on the last day, in shorter months.',
            lastBalanceLabel: 'Last outstanding balance (optional)',
            editMonthlyHint: 'Applies to installments still due — paid ones stay as they are.',
            editNextDueHelper: 'Installments still due are rescheduled from this date.',
            editPaidPrefix: '{{paid}} paid',
            editTotalHelperEditable: 'The total can change while no installment has been paid.',
            editTotalHelperLocked: 'The total locks after the first payment or prepayment.',
            editTotalRequired: 'Enter the total number of installments.',
            editTotalTooLow: 'The total cannot be lower than installment {{number}}.',
        },
        loan: {
            toggle: 'Someone owes me this',
            toggleHint: 'Becomes a receivable in "Owed to me", outside spending reports',
            amountHint: 'Stays out of spending reports — becomes a receivable in "Owed to me".',
            toWhom: 'To whom',
            personPlaceholder: "Person's name",
            scheduleTitle: 'Repayment schedule (optional)',
            scheduleHint: 'When you agreed on payment — a date, monthly installments, or "whenever".',
            existingTitle: 'The money never left my account',
            existingHint: 'A sale, a split bill, or an outflow before the app — just records the balance, without debiting an account',
        },
        refund: {
            title: "It's a refund / reversal",
            hint: 'Offsets the category spending in the report',
        },
        invoiceMonth: {
            refundPrefix: 'Offsets the invoice of',
            expensePrefix: 'Goes into the invoice of',
            paidWarning: 'This invoice is already paid. The amount will sit as leftover on it and will not become an automatic credit on the next one.',
        },
        ignore: {
            title: 'Ignore',
            hint: 'Not counted in reports or the monthly net. Still in your balance and statement.',
            a11y: 'Ignore entry in analysis',
        },
        yieldReserva: {
            title: 'Yield of the {{name}} pocket',
            hint: 'It stays saved there and counts in the pocket, not in your income. Amount and deleting are handled there.',
        },
        convertToTransfer: {
            title: 'Convert to transfer',
            hintExpense: 'Did this money go to another of your accounts? Make it a transfer.',
            hintIncome: 'Did this money come from another of your accounts? Make it a transfer.',
            a11y: 'Convert entry to transfer',
            pickDestination: 'Which account did it go to?',
            pickSource: 'Which account did it come from?',
            confirmHint: 'Check where it leaves from and where it goes. Tap an account to change it.',
            from: 'From',
            to: 'To',
            swap: 'Swap source and destination',
            mergeToggle: 'Merge with the other side',
            mergeFound: '{{description}} · {{amount}} on {{date}}, in {{account}}',
            mergeOnHint: 'The other entry leaves the list; both balances stay the same.',
            mergeOffHint: 'Without merging, the other account counts the money twice: the existing entry plus the transfer.',
            confirm: 'Convert',
        },
        recurrence: {
            title: 'Repeat',
            once: 'Once',
            installment: 'Installments',
            recurring: 'Recurring',
            frequency: 'Frequency',
            weeklyChip: 'Weekly',
            biweeklyChip: 'Every 2 weeks',
            monthlyChip: 'Monthly',
            bimonthlyChip: 'Bimonthly',
            customChip: 'Custom',
            repeatsEvery: 'Repeats every',
            unitDay: 'Day',
            unitWeek: 'Week',
            unitMonth: 'Month',
            unitYear: 'Year',
            ends: 'Ends',
            endsNever: 'Never',
            endsOnDate: 'On a date',
            endsAfterCount: 'After N occurrences',
            chooseDate: 'Choose',
            preview: 'Next: {{dates}}',
            transferInfo: 'The transfer will follow a rule, without pre-creating 12 entries.',
            generalInfo: 'The app stores the rule and materializes upcoming occurrences as they come due.',
            monthlyDayLabel: 'On day {{day}}',
            nthWeekdayLabel: 'On the {{ordinal}} {{weekday}}',
            weekdays: {
                sunday: 'Sunday',
                monday: 'Monday',
                tuesday: 'Tuesday',
                wednesday: 'Wednesday',
                thursday: 'Thursday',
                friday: 'Friday',
                saturday: 'Saturday',
            },
            weekdayInitials: {
                sunday: 'S',
                monday: 'M',
                tuesday: 'T',
                wednesday: 'W',
                thursday: 'T',
                friday: 'F',
                saturday: 'S',
            },
            ordinals: {
                first: '1st',
                second: '2nd',
                third: '3rd',
                fourth: '4th',
                fifth: '5th',
                last: 'last',
            },
        },
        recurringSummary: {
            title: 'Recurrence',
            info: '{{frequency}} · changes follow the options: only this, future, or all.',
        },
        installment: {
            count: 'Installments',
            current: 'Current installment',
            valueType: 'Entered amount',
            valueTotal: 'Total',
            valuePerInstallment: 'Per installment',
            fromInstallmentNotice: 'The app will create from installment {{current}}/{{total}} onward, no backdating.',
            previewFrom: 'From {{current}}/{{total}}: {{trackedCount}}x of {{amount}} in the app ({{trackedAmount}} projected)',
            previewSimple: '{{times}}x of {{amount}}',
            previewWithTotal: '{{times}}x of {{amount}} = {{totalAmount}}',
            decreaseCountA11y: 'One fewer installment',
            increaseCountA11y: 'One more installment',
            decreaseCurrentA11y: 'Previous current installment',
            increaseCurrentA11y: 'Next current installment',
        },
        validation: {
            alertTitle: 'Attention',
            ok: 'OK',
            checkForm: 'Check the form',
            selectAccount: 'Select an account',
            selectCreditCard: 'Select a credit card',
            loanPersonRequired: 'Enter who owes you',
            loanScheduleSum: 'The payment schedule must add up to the amount owed.',
        },
        toast: {
            installmentCreated: 'Installment plan created',
            installmentSaved: 'Installment plan saved',
            receiptImported: 'Receipt imported',
            saveFailed: "Couldn't save",
            transactionGone: "This transaction doesn't exist anymore. Open it again from the list.",
        },
        receiptImport: {
            pdfExtractError: "Couldn't extract the amount from this PDF.",
            imageExtractError: "Couldn't extract the amount from this receipt. Try a sharper image.",
            cameraPermission: 'Allow camera access to photograph the receipt.',
        },
        importSheet: {
            title: 'Import receipt',
            message: 'We read the amount, description, and date from the receipt and fill them in right here.',
            gallery: 'Gallery',
            galleryDescription: 'Screenshot or photo of the receipt',
            camera: 'Camera',
            cameraDescription: 'Take a photo now',
            pdf: 'PDF',
            pdfDescription: 'Bank receipt file',
        },
        scanOverlay: 'Reading receipt...',
        spendGuard: {
            verbTransfer: 'transfer',
            verbSpend: 'spend',
        },
        deleteDialog: {
            title: 'Delete transaction',
            message: 'The transaction goes to the trash and the account balance is recalculated. You can undo it from the notice that appears right after.',
            confirm: 'Delete',
        },
        recurringEditSheet: {
            title: 'Edit transaction',
            message: 'This transaction repeats. What do you want to edit?',
            onlyThis: 'Only this one',
            onlyThisDescription: 'Changes only this entry.',
            thisAndFuture: 'This and future',
            thisAndFutureDescription: 'Keeps the history and updates from here on.',
            all: 'All',
            allDescription: 'Reapplies the change to the entire series.',
            allConfirmTitle: 'Change the whole series?',
            allConfirmMessage_one: 'This rewrites the entry already recorded in this series, which is already done, plus all future ones. To keep what is already done, use "This and future".',
            allConfirmMessage_other: 'This rewrites the {{count}} entries already recorded in this series (already done: {{settled}}) plus all future ones. To keep what is already done, use "This and future".',
            allConfirmAction: 'Change all',
            paidInvoiceMessage: 'This installment repeats. Moving the whole series would touch an invoice you already paid, so that scope is not offered here.',
        },
        installmentCardRemovalSheet: {
            message: 'You are taking installments off the card. They become expenses on the chosen account — future ones stay pending until their dates.',
            onlyThisDescription: 'Only this installment leaves the invoice and goes to the account.',
            thisAndFutureDescription: 'This and the next ones leave the invoices and go to the account.',
            allDescription: 'The whole purchase leaves the card and the invoices are recalculated.',
        },
        recurringDeleteSheet: {
            title: 'Delete transaction',
            message: 'This transaction repeats. What do you want to delete?',
            projectedMessage: "This charge hasn't happened yet. What do you want to cancel?",
            onlyThis: 'Only this one',
            onlyThisDescription: 'Removes only this entry.',
            projectedOnlyThisDescription: 'Skips only this charge; the subscription continues.',
            thisAndNext: 'This and the next',
            thisAndNextDescription: 'Deletes this occurrence and the following ones.',
            fromThisDate: 'From this date on',
            fromThisDateDescription: 'Keeps what already happened and cancels from here on.',
            all: 'All',
            allDescription: 'Deletes the entire recurring series.',
        },
    },
    amountInput: {
        keypad: {
            openA11y: 'Edit the amount with the calculator keypad',
            add: 'Add',
            subtract: 'Subtract',
            multiply: 'Multiply',
            divide: 'Divide',
            decimal: 'Decimal separator',
            backspace: 'Delete',
            backspaceHint: 'Tap deletes one digit; hold to clear everything',
            confirm: 'Confirm',
        },
    },
    newTransactionMenu: {
        pocket: 'Pocket',
        transfer: 'Transfer',
        income: 'Income',
        creditExpense: 'Card expense',
        expense: 'Expense',
        cardExpense: 'Expense',
        cardRefund: 'Refund',
        close: 'Close',
    },
    periodSummary: {
        neutralTitle: 'Neutral balance',
        neutralSubtitle: 'No money in or out in this view',
    },
    quickPocket: {
        stepReserva: 'Save to which pocket?',
        stepSource: 'From which account?',
        stepAmount: 'Save to pocket',
        ofTarget: 'of {{amount}}',
        originLabel: 'Where does the money come from?',
        originAvailable: 'Available',
        originNew: 'New money',
        originTransfer: 'Other account',
        newHint: 'Added to {{account}} as a balance adjustment and saved into the pocket right away.',
        transferHint: 'Transfers from {{source}} to {{account}} and the amount is saved into the pocket right away.',
        chooseSource: 'Choose source account',
        submitTransfer: 'Transfer and save',
    },
    quickTransfer: {
        defaultDescription: 'Transfer',
        descriptionWithAccounts: 'Transfer {{source}} → {{destination}}',
        today: 'Today',
        scheduledSuffix: ' • Scheduled',
        stepSource: 'Source account',
        stepDestination: 'Destination account',
        stepAmount: 'Transfer amount',
        amountLabel: 'Amount',
        reservedCaption: '{{amount}} reserved',
        submitRecurring: 'Create recurring',
        submitScheduled: 'Schedule transfer',
        submitTransfer: 'Transfer',
        frequent: 'Frequent',
        dateLabel: 'Date',
        descriptionLabel: 'Description',
        recurringInfo: 'The transfer will be created automatically each period.',
        reservasSection: 'Pockets',
        reservaSameAccount: 'This account · just saves',
        reservaA11y: 'Pocket {{name}}, in {{account}}',
    },
    settleSheet: {
        actionTransfer: 'Transfer',
        actionPay: 'Pay',
        actionReceive: 'Receive',
        amountLabelTransfer: 'Transfer amount',
        amountLabelPay: 'Payment amount',
        amountLabelReceive: 'Amount received',
        dateFallback: 'Date',
        accountFrom: 'From',
        account: 'Account',
        accountTo: 'To',
        selectAccountPlaceholder: 'Select account...',
        scopeHint: "Since the amount or account changed, you'll choose the scope in the next step.",
        sourceAccountTitle: 'Source account',
        destinationAccountTitle: 'Destination account',
        scopeSheetTitle: 'Apply adjustment',
        scopeSheetMessage: 'This transaction is part of a series. Where do you want to apply the change?',
        scopeSingle: 'Only this one',
        scopeFuture: 'This and future',
        scopeAll: 'All',
        partial: {
            modePartial: 'Partial payment',
            modeFull: 'Settle anyway',
            hintPartial: 'The installment stays open with {{remaining}}.',
            hintFull: 'The installment becomes {{amount}} and is settled.',
        },
        charges: {
            action: 'Interest and late fee',
            hint: 'Late charges on this installment',
        },
    },
    spendGuard: {
        verbTransfer: 'transfer',
        verbSpend: 'spend',
        rescueNotesTransfer: 'For a transfer',
        rescueNotesExpense: 'For an expense',
        verbTransferCap: 'Transfer',
        verbSpendCap: 'Spend',
        availableTitle: 'Only {{amount}} available in this account',
        submergedTitle: 'Pockets already exceed this account balance by {{deficit}}',
        reservedBody_one: '{{reserved}} is saved in a pocket. You need {{shortfall}} more to {{verb}} {{amount}}.',
        reservedBody_other: '{{reserved}} are saved in pockets. You need {{shortfall}} more to {{verb}} {{amount}}.',
        proceedAnyway: '{{verb}} anyway',
        proceedAnywayHint: 'The account may go negative',
        takeFromReserva: 'Take from a pocket to cover it',
        rescueExact: 'Withdraw {{amount}}',
        rescuePartial: "Withdraw what's available ({{amount}})",
        cancel: 'Cancel',
        pickTitle: 'From which pocket?',
        pickBody: "We'll withdraw {{amount}} to cover the {{target}}.",
        targetTransfer: 'transfer',
        targetExpense: 'expense',
        reservaGuarded: '{{amount}} saved',
        back: 'Back',
    },
    filters: {
        title: 'Filter transactions',
        clear: 'Clear',
        // viewCount_zero: mirrors pt-BR, where 0 would fall into _one ("Ver 0 transação").
        viewCount_zero: 'View {{count}} transactions',
        viewCount_one: 'View {{count}} transaction',
        viewCount_other: 'View {{count}} transactions',
        applyGeneric: 'View transactions',

        // Accounts and cards are multi-select (ADR 0170): plural label, like "Tags".
        accountLabel: 'Accounts',
        allAccounts: 'All accounts',
        accountModalTitle: 'Filter by account',
        accountsSelected_one: '{{count}} account',
        accountsSelected_other: '{{count}} accounts',
        cardLabel: 'Cards',
        allCards: 'All cards',
        cardModalTitle: 'Filter by card',
        cardsSelected_one: '{{count}} card',
        cardsSelected_other: '{{count}} cards',

        categoryLabel: 'Category',
        allCategories: 'All categories',
        categoryModalTitle: 'Filter by category',
        categorySearchPlaceholder: 'Search category or subcategory',
        categoryTypeExpensePlural: 'Expenses',
        categoryTypeIncomePlural: 'Income',
        noCategoryFound: 'No category found',

        subcategoryLabel: 'Subcategory',
        allSubcategories: 'All subcategories',
        subcategoryModalTitle: 'Filter by subcategory',
        subcategorySearchPlaceholder: 'Search subcategory',

        tagsLabel: 'Tags',
        allTags: 'All',
        tagsModalTitle: 'Filter by tag',
        tagSearchPlaceholder: 'Search tag',
        tagsSelected_one: '{{count}} tag',
        tagsSelected_other: '{{count}} tags',
        noTagsYet: "You haven't created any tags yet",

        amountLabel: 'Amount',
        anyAmount: 'Any amount',
        amountModalTitle: 'Amount range',
        minAmount: 'Minimum',
        maxAmount: 'Maximum',
        amountBetween: '{{min}} to {{max}}',
        amountFrom: 'From {{min}}',
        amountUpTo: 'Up to {{max}}',
        clearRange: 'Clear',
        applyRange: 'Apply',

        sortLabel: 'Sorting',
        sortModalTitle: 'Sort by',
        sortDirection: 'Direction',
        sortDate: 'Date',
        sortAmount: 'Amount',
        sortCategory: 'Category',
        ascending: 'Ascending',
        descending: 'Descending',

        displayLabel: 'Display and search',
        displayNone: 'None active',
        displayActive_one: '{{count}} active',
        displayActive_other: '{{count}} active',
        displayTitle: 'Display and search',
        groupSearch: 'Search',
        groupStatement: 'Statement',
        groupInTransactions: 'On transactions',
        groupBalances: 'Balances',
        groupBalancesHint: 'These preferences always apply — clearing the filters does not turn them off.',
        searchInNotes: 'Search in notes',
        searchInNotesTip: 'Search starts looking at each entry\'s notes too, not only the description.',
    },
    listItem: {
        // Receita de rendimento de caixinha na lista (ADR 0164): linha da caixinha, como guardar/resgatar.
        reservaYield: 'Pocket yield',
        noCategory: 'No category',
        confirmA11y: 'Mark as done',
        unconfirmA11y: 'Mark as pending',
        subtitle: {
            invoice: 'Statement · {{account}}',
            invoicePayment: 'Statement payment · {{account}}',
            invoiceManualAdjustment: 'Manual statement adjustment',
            loanDisbursement: 'Receivable',
            loanScheduledReceipt: 'Agreed date',
            loanReceipt: 'Payment from someone',
            balanceAdjustment: 'Balance adjustment',
            transfer: '{{source}} → {{destination}}',
        },
        prepayment: 'Paydown',
        partialPayment: 'Partial payment',
        financing: 'Financing',
        status: {
            ignored: 'Ignored',
            manual: 'Manual',
            manualAdjustment: 'Manual adjustment',
            manualDecrease: 'Downward adjustment',
            previousBalance: 'Previous balance',
            invoice: 'Statement',
            settlement: 'Settlement',
            loan: 'Receivable',
            excess: 'Surplus',
            writeOff: 'Write-off',
            credit: 'Credit',
            refund: 'Refund',
            adjustment: 'Adjustment',
            pending: 'Pending',
            recurring: 'Recurring',
        },
    },
} satisfies DeepPartialStrings<TranslationSchema['transactions']>;
